"use client";

import * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import Link from "next/link";

const inlineVariant = tv({
  base: "flex w-fit flex-row items-center gap-1 font-montserrat transition duration-300 ease-out",
  variants: {
    variant: {
      default: "underline decoration-1 underline-offset-2",
      reversed:
        "border border-transparent px-1 py-2 font-normal decoration-1 underline-offset-2 !outline-0 !ring-0 hover:!underline focus:!rounded-lg focus:border-neutral-500 focus:!underline active:!rounded-lg active:!border-primary-300 active:bg-transparent active:underline",
    },
    color: {
      orange: [
        "border border-transparent bg-transparent text-primary-500",
        "hover:!text-primary-600 hover:no-underline",
        "focus:bg-primary-200 focus:text-primary-600 focus:no-underline",
        "active:border active:border-primary-500 active:bg-transparent active:text-primary-600 active:no-underline",
      ],
      teal: [
        "border border-transparent bg-transparent text-secondary-500",
        "hover:!text-secondary-600 hover:no-underline",
        "focus:bg-secondary-200 focus:text-secondary-600 focus:no-underline",
        "active:border active:border-secondary-500 active:bg-transparent active:text-secondary-600 active:no-underline",
      ],
      gray: [
        "border border-transparent bg-transparent text-neutral-500",
        "hover:!text-neutral-600 hover:no-underline",
        "focus:bg-neutral-200 focus:text-neutral-600 focus:no-underline",
        "active:border active:border-neutral-500 active:bg-transparent active:text-neutral-600 active:no-underline",
      ],
      "orange-light": [
        "border border-transparent bg-transparent text-primary-200",
        "hover:!text-primary-100 hover:no-underline",
        "focus:bg-primary-500 focus:text-primary-100 focus:no-underline",
        "active:border active:border-primary-300 active:bg-transparent active:text-primary-100 active:no-underline",
      ],
    },
    size: {
      lg: "rounded-lg px-2 py-2 text-button-1 active:rounded-xl",
      md: "rounded-[4px] px-2 py-1 text-button-2 active:rounded-lg",
      sm: "rounded-sm p-0 text-button-3 active:rounded-[4px]",
    },
    disabled: {
      true: "disabled:cursor-not-allowed disabled:border-0 disabled:bg-transparent disabled:text-neutral-300 disabled:underline disabled:underline-offset-2 disabled:shadow-none disabled:ring-0",
    },
  },
  compoundVariants: [
    {
      variant: "reversed",
      class: "!text-neutral-500 focus:!bg-transparent",
    },
    {
      variant: "reversed",
      disabled: true,
      class:
        "!bg-transparent !text-neutral-400 !no-underline hover:!no-underline active:!bg-transparent",
    },
  ],
  defaultVariants: {
    variant: "default",
    color: "orange",
    size: "md",
  },
});

type InlineButtonVariant = VariantProps<typeof inlineVariant>;

export interface InlineButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    InlineButtonVariant {
  asChild?: boolean;
  href?: string;
}

const InlineButton = React.forwardRef<HTMLButtonElement, InlineButtonProps>(
  (
    {
      className,
      variant,
      color,
      size,
      disabled,
      children,
      href,
      asChild = Boolean(href),
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          inlineVariant({ variant, color, size, disabled, className })
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {href ? <Link href={href}>{children}</Link> : children}
      </Comp>
    );
  }
);
InlineButton.displayName = "InlineButton";

export default InlineButton;
